// Get the file extension for a given mime type
// From: https://support.bandwidth.com/hc/en-us/articles/360014128994-What-MMS-file-types-are-supported-

const FILE_EXTENSIONS: { [mimeType: string]: string } = {
  "application/json": ".json",
  "application/ogg": ".ogv",
  "application/pdf": ".pdf",
  "application/rtf": ".rtf",
  "application/zip": ".zip",
  "application/x-tar": ".tar",
  "application/xml": ".xml",
  "application/gzip": ".gz",
  "application/x-bzip2": ".bz2",
  "application/x-gzip": ".gz",
  "application/smil": ".smil",
  "application/javascript": ".js",
  "audio/mp4": ".m4a",
  "audio/mpeg": ".mp3",
  "audio/ogg": ".oga",
  "audio/flac": ".flac",
  "audio/webm": ".webm",
  "audio/wav": ".wav",
  "audio/amr": ".amr",
  "audio/3gpp": ".3gp",
  "image/bmp": ".bmp",
  "image/gif": ".gif",
  "image/jpeg": ".jpeg",
  "image/pjpeg": ".pjpeg",
  "image/png": ".png",
  "image/svg+xml": ".svg",
  "image/tiff": ".tiff",
  "image/webp": ".webp",
  "image/x-icon": ".ico",
  "text/css": ".css",
  "text/csv": ".csv",
  "text/calendar": ".ics",
  "text/plain": ".txt",
  "text/javascript": ".js",
  "text/vcard": ".vcf",
  "text/vnd.wap.wml": ".wap",
  "text/xml": ".xml",
  "video/avi": ".avi",
  "video/mp4": ".mp4",
  "video/mpeg": ".mpg",
  "video/ogg": ".ogv",
  "video/quicktime": ".mov",
  "video/webm": ".webm",
  "video/x-ms-wmv": ".wmv",
  "video/x-flv": ".flv",
};

// Inverted version of FILE_EXTENSIONS where the keys are extensions
// and the values are mime types (doubles are overwritten by the latest).
const MIME_TYPES_BY_EXTENSION = Object.keys(FILE_EXTENSIONS).reduce(
  (acc, key) => {
    acc[FILE_EXTENSIONS[key]] = key;
    return acc;
  },
  { ".jpg": "image/jpeg" } as { [extension: string]: string }
);

export const ALLOWED_MMS_ATTACHMENT_CONTENT_TYPES = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/gif",
  "text/vcard",
  "video/mp4",
];

export function getMMSTypeFromContentType(
  contentType: string
): "image" | "video" | "contact" {
  switch (contentType) {
    case "image/jpg":
    case "image/jpeg":
    case "image/png":
    case "image/gif":
      return "image";
    case "text/vcard":
      return "contact";
    case "video/mp4":
      return "video";
    default:
      throw new Error(`Unknown MMS content type: ${contentType}`);
  }
}

export function getFileExtensionFromContentType(rawMimeType: string): string {
  let mimeType = rawMimeType.toLowerCase().trim();
  if (mimeType.includes(";")) {
    mimeType = mimeType.split(";")[0].trim();
  }

  if (
    !mimeType ||
    !mimeType.includes("/") ||
    mimeType === "application/octet-stream"
  ) {
    return "";
  }

  if (FILE_EXTENSIONS[mimeType]) {
    return FILE_EXTENSIONS[mimeType];
  }

  return mimeType.split("/").pop()!;
}

export function getMimeTypeFromUrl(url: string): string | null {
  if (!isValidUrl(url)) {
    return null;
  }

  const path = new URL(url).pathname;
  const extension =
    path && path.split(".").length > 1
      ? path.split(".").at(-1)?.toLocaleLowerCase()
      : null;

  if (!extension) {
    return null;
  }

  return MIME_TYPES_BY_EXTENSION[`.${extension}`] || null;
}

// Polyfill for URL.canParse
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch (_err) {
    return false;
  }
}
